import React, { useState, useEffect } from 'react';
import {
  Share,
  Calendar,
  X,
  ChevronDown,
  ArrowUpDown,
  Edit2,
  Trash2,
  MoreVertical,
  Trash,
  Download,
  CalendarSync,
  Edit,
} from 'lucide-react';
import socialMediaService, { IUserPost } from 'features/social-media/services/socialMediaCommonApi';

const Menu = ({
  status,
  view = 'scheduled',
}: {
  status: string;
  view: string;
}) => {
  const handleGetPosts = () => {
    socialMediaService.getUserPosts().then((res) => {
      console.log(res);
    });
  };
  const [isOpen, setIsOpen] = useState(false);

  const toggleMenu = () => setIsOpen(!isOpen);

  const renderPlatformMenu = () => (
    <div className="p-4 space-y-4 text-text-primary bg-background-card">
      <button className="w-full px-4 py-3 flex items-center gap-3  hover:bg-gray-100 rounded-lg">
        <Edit className="text-primary h-5 w-5" />
        <span>Edit</span>
      </button>
      <button className="w-full px-4 py-3 flex items-center gap-3  hover:bg-gray-100 rounded-lg">
        <Download className="text-primary h-5 w-5" />
        <span>Export</span>
      </button>
      <button className="w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-100 rounded-lg">
        <Calendar className="text-primary h-5 w-5" />
        <span>Publish/Schedule</span>
      </button>
      <button className="w-full px-4 py-3 flex items-center gap-3  hover:bg-gray-100 rounded-lg">
        <Trash className="text-primary h-5 w-5" />
        <span>Delete</span>
      </button>
    </div>
  );

  const renderScheduledMenu = () => (
    <div className="p-4 space-y-4 bg-background-card text-text-primary">
      <button className="w-full px-4 py-3 flex items-center gap-3  hover:bg-gray-100 rounded-lg">
        <Download className="text-primary h-5 w-5" />
        Export
      </button>

      {status === 'Scheduled' && (
        <>
          <button className="w-full px-4 py-3 flex items-center gap-3  hover:bg-gray-100 rounded-lg">
            <CalendarSync className="text-primary h-5 w-5" />
            <span>Reschedule</span>
          </button>
          <button className="w-full px-4 py-3 flex items-center gap-3  hover:bg-gray-100 rounded-lg">
            <X className="text-primary h-5 w-5" />
            <span>Cancel</span>
          </button>
        </>
      )}

      {status === 'Published' && (
        <button className="w-full px-4 py-3 flex items-center gap-3 text-gray-700 hover:bg-gray-100 rounded-lg">
          <Trash className="text-indigo-500 h-5 w-5" />
          <span>Delete</span>
        </button>
      )}
    </div>
  );

  

  return (
    <>
      <button
        onClick={toggleMenu}
        className="p-2 hover:bg-gray-100 rounded-full"
      >
        <MoreVertical className="text-indigo-500 h-6 w-6" />
      </button>

      {isOpen && (
        <>
          <div
            className="fixed z-30 inset-0 bg-black/20"
            onClick={toggleMenu}
          />
          <div className="fixed z-50 bottom-0 left-0 right-0 bg-background-card rounded-t-xl shadow-lg">
            <div className="w-12 h-1 bg-white rounded-full mx-auto my-3" />
            {view === 'platform' ? renderPlatformMenu() : renderScheduledMenu()}
          </div>
        </>
      )}
    </>
  );
};

const PostManagementTable = () => {
  const [currentView, setCurrentView] = useState('LinkedIn');
  const [mobileTab, setMobileTab] = useState('LinkedIn');
  const [posts, setPosts] = useState<IUserPost[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const response = await socialMediaService.getUserPosts();
      setPosts(response.data);
    } catch (error) {
      console.error('Error fetching posts:', error);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchPosts();
  }, []);

  // Filter posts based on current view
  const filteredPosts = posts.filter(post => {
    if (currentView === 'ScheduledPublished') {
      return true; // Show all posts in scheduled/published view
    }
    return post.platform_name.toLowerCase() === currentView.toLowerCase();
  });

  // Platform view data (LinkedIn/X)
  const platformPosts = filteredPosts.filter(post => post.status === 'saved');

  // Scheduled/Published view data
  const scheduledPosts = filteredPosts.filter(post => 
    post.status === 'scheduled' || post.status === 'published'
  );

  const getStatusColor = (status: string) => {
    return status === 'published'
      ? 'bg-gray-100 text-gray-600'
      : 'bg-purple-50 text-[#846CEC]';
  };

  const renderPlatformView = () => (
    <table className="w-full bg-background text-text-primary">
      <thead>
        <tr className="text-left text-primary ">
          <th className="pl-6 pr-4 py-4 font-medium">Post Name</th>
          <th className="px-4 py-4 font-medium flex items-center gap-1">
            Date Created <ArrowUpDown size={16} />
          </th>
          <th className="px-4 py-4 font-medium">Actions</th>
        </tr>
      </thead>
      <tbody>
        {platformPosts.map((post) => (
          <tr
            key={post.id}
            className="border-t border-primary hover:bg-gray-50"
          >
            <td className="pl-6 pr-4 py-4">
              <div className="flex items-center gap-3">
                <input type="checkbox" className="rounded border-gray-300" />
                <span>{post.post_name}</span>
              </div>
            </td>
            <td className="px-4 py-4 ">{new Date(post.created_at).toLocaleDateString('en-US', { weekday: 'short', day: '2-digit', month: 'short', year: '2-digit' })}</td>
            <td className="px-4 py-4">
              <div className="flex items-center gap-2">
                <button className="p-2 text-primary hover:bg-gray-100 rounded-md">
                  <Edit2 size={18} />
                </button>
                <button className="p-2 text-primary hover:bg-gray-100 rounded-md">
                  <Share size={18} />
                </button>
                <button className="p-2 text-primary hover:bg-gray-100 rounded-md">
                  <Calendar size={18} />
                </button>
                <button className="p-2 text-primary hover:bg-gray-100 rounded-md">
                  <Trash2 size={18} />
                </button>
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  const renderScheduledView = () => (
    <table className="w-full bg-background">
      <thead>
        <tr className="text-left text-primary">
          <th className="pl-6 pr-4 py-4 font-medium ">Post Name</th>
          <th className="px-4 py-4 font-medium">
            <div className="flex items-center gap-1">
              Platform <ChevronDown size={16} />
            </div>
          </th>
          <th className="px-4 py-4 font-medium">
            <div className="flex items-center gap-1">
              Date Created <ArrowUpDown size={16} />
            </div>
          </th>
          <th className="px-4 py-4 font-medium">
            <div className="flex items-center gap-1">
              Status Date <ArrowUpDown size={16} />
            </div>
          </th>
          <th className="px-4 py-4 font-medium">Actions</th>
        </tr>
      </thead>
      <tbody>
        {scheduledPosts.map((post) => (
          <tr
            key={post.id}
            className="border-t border-primary text-text-primary "
          >
            <td className="pl-6 pr-4 py-4">
              <div className="flex items-center gap-3">
                <input type="checkbox" className="rounded border-gray-300" />
                <span>{post.post_name}</span>
              </div>
            </td>
            <td className="px-4 py-4 ">{post.platform_name}</td>
            <td className="px-4 py-4 ">{new Date(post.created_at).toLocaleDateString('en-US', { weekday: 'short', day: '2-digit', month: 'short', year: '2-digit' })}</td>
            <td className="px-4 py-4">
              <div className="flex items-center gap-2">
                <span>
                  {post.status === 'published' 
                    ? (post.published_at ? new Date(post.published_at).toLocaleDateString('en-US', { weekday: 'short', day: '2-digit', month: 'short', year: '2-digit' }) : '-')
                    : (post.scheduled_at ? new Date(post.scheduled_at).toLocaleDateString('en-US', { weekday: 'short', day: '2-digit', month: 'short', year: '2-digit' }) : '-')
                  }
                </span>
                <span
                  className={`px-2 py-1 text-sm rounded-md ${getStatusColor(post.status)}`}
                >
                  {post.status.charAt(0).toUpperCase() + post.status.slice(1)}
                </span>
              </div>
            </td>
            <td className="px-4 py-4">
              <div className="flex items-center gap-2">
                <button className="p-2 text-primary hover:bg-gray-100 rounded-md">
                  <Share size={18} />
                </button>
                <button className="p-2 text-primary hover:bg-gray-100 rounded-md">
                  <Calendar size={18} />
                </button>
                <button className="p-2 text-primary hover:bg-gray-100 rounded-md">
                  <X size={18} />
                </button>
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  // Mobile Components
  const MobilePostCard = ({
    post,
    view,
  }: {
    post: IUserPost;
    view: 'platform' | 'scheduled';
  }) => (
    <div className="border-b p-4 text-text-primary">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <input type="checkbox" className="rounded border-gray-300" />
          <span className="font-medium">{post.post_name}</span>
        </div>
        <div className="flex items-center gap-3">
          {view === 'scheduled' && (
            <span
              className={`px-2 py-1 text-sm rounded-md ${getStatusColor(post.status)}`}
            >
              {post.status.charAt(0).toUpperCase() + post.status.slice(1)}
            </span>
          )}
          <Menu status={post.status} view={view} />
        </div>
      </div>
      <div className="space-y-1 text-sm ">
        {view === 'scheduled' && <p>Platform: {post.platform_name}</p>}
        <p>Created: {new Date(post.created_at).toLocaleDateString('en-US', { weekday: 'short', day: '2-digit', month: 'short', year: '2-digit' })}</p>
        {view === 'scheduled' && (
          <p>
            {post.status === 'published' ? 'Published' : 'Scheduled'} Date: {
              post.status === 'published'
                ? (post.published_at ? new Date(post.published_at).toLocaleDateString('en-US', { weekday: 'short', day: '2-digit', month: 'short', year: '2-digit' }) : '-')
                : (post.scheduled_at ? new Date(post.scheduled_at).toLocaleDateString('en-US', { weekday: 'short', day: '2-digit', month: 'short', year: '2-digit' }) : '-')
            }
          </p>
        )}
      </div>
    </div>
  );

  // Mobile Header Tabs
  const MobileHeader = () => (
    <div className="lg:hidden">
      <div className="flex w-full bg-background p-4 text-lg">
        <button
          className={`flex-1 py-2 text-center font-medium ${
            mobileTab === 'LinkedIn'
              ? 'text-[#846CEC] border-b-2 border-[#846CEC]'
              : 'text-gray-400'
          }`}
          onClick={() => setMobileTab('LinkedIn')}
        >
          LinkedIn
        </button>
        <button
          className={`flex-1 py-2 text-center font-medium ${
            mobileTab === 'X'
              ? 'text-[#846CEC] border-b-2 border-[#846CEC]'
              : 'text-gray-400'
          }`}
          onClick={() => setMobileTab('X')}
        >
          X
        </button>
        <button
          className={`flex-1 py-2 text-center font-medium ${
            mobileTab === 'Publish'
              ? 'text-[#846CEC] border-b-2 border-[#846CEC]'
              : 'text-gray-400'
          }`}
          onClick={() => setMobileTab('Publish')}
        >
          Publish
        </button>
      </div>
    </div>
  );

  const MobileView = () => (
    <div className="lg:hidden bg-background">
      <MobileHeader />

      {mobileTab === 'LinkedIn' && (
        <div className="">
          {platformPosts
            .filter(post => post.platform_name.toLowerCase() === 'linkedin')
            .map((post) => (
              <MobilePostCard
                key={post.id}
                post={post}
                view="platform"
              />
            ))}
        </div>
      )}

      {mobileTab === 'X' && (
        <div className="">
          {platformPosts
            .filter(post => post.platform_name.toLowerCase() === 'x')
            .map((post) => (
              <MobilePostCard
                key={post.id}
                post={post}
                view="platform"
              />
            ))}
        </div>
      )}

      {mobileTab === 'Publish' && (
        <div className="">
          {scheduledPosts.map((post) => (
            <MobilePostCard key={post.id} post={post} view="scheduled" />
          ))}
        </div>
      )}
    </div>
  );

  // Desktop View
  const DesktopView = () => (
    <div className="hidden lg:block">
      <div className="flex items-center mb-6">
        <button
          className={`px-6 py-2 ${
            currentView === 'LinkedIn'
              ? 'bg-primary text-white'
              : 'bg-secondary text-primary'
          }`}
          onClick={() => setCurrentView('LinkedIn')}
        >
          LinkedIn
        </button>
        <button
          className={`px-6 py-2 ${
            currentView === 'X'
              ? 'bg-primary text-white'
              : 'bg-secondary text-primary'
          }`}
          onClick={() => setCurrentView('X')}
        >
          X
        </button>
        <button
          className={`px-6 py-2 ${
            currentView === 'ScheduledPublished'
              ? 'bg-primary text-white'
              : 'bg-secondary text-primary'
          }`}
          onClick={() => setCurrentView('ScheduledPublished')}
        >
          Scheduled and published
        </button>
        <div className="flex-grow"></div>
        <button className="text-primary hover:underline flex items-center gap-2">
          + New {currentView === 'ScheduledPublished' ? '' : currentView} post
        </button>
      </div>

      <div className="bg-white rounded-lg shadow">
        {currentView === 'ScheduledPublished'
          ? renderScheduledView()
          : renderPlatformView()}
      </div>
    </div>
  );

  return (
    <div className="w-full max-w-[1400px] mx-auto">
      <div className="p-4 lg:p-6 text-text-primary">
        <h1 className="text-xl lg:text-2xl font-bold mb-2">Your Posts</h1>
        <p className=" mb-6">
          All your files are safely stored here in one place.
        </p>
        <button 
          onClick={fetchPosts}
          className="px-4 py-2 bg-primary text-white rounded hover:bg-primary/90"
        >
          Get All Posts
        </button>
      </div>
      <div className="max-w-[1200px] mx-auto">
        <MobileView />
        <DesktopView />
      </div>
    </div>
  );
};

export default PostManagementTable;
